import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import axios from 'axios';
import config from '../config.json';
import { createAuth } from '../Helpers';
import { Person } from '../domain/Person';

export const reducerName = 'personState';

export interface PersonState {
    persons: { [key: string]: Person & { showRadial?: boolean } };
    status: 'idle' | 'loading' | 'succeeded' | 'failed';
    error: string | null;
    redirectAfterError: string | null;
}

const initialState: PersonState = {
    persons: {},
    status: 'idle',
    error: null,
    redirectAfterError: null,
};

export const getPerson = createAsyncThunk(
    'personState/getPerson',
    async (personId: string, { getState, rejectWithValue }) => {
        try {
            const response = await axios({
                method: 'GET',
                url: `${config.server}/api/person/${personId}`,
                withCredentials: true,
                auth: createAuth(getState),
            });
            return response.data;
        } catch (error) {
            const err = error as any;
            return rejectWithValue(err.response?.data || 'An error occurred');
        }
    }
);

export const getAllPersons = createAsyncThunk(
    'personState/getAllPersons',
    async (_, { getState, rejectWithValue }) => {
        try {
            const response = await axios({
                method: 'GET',
                url: `${config.server}/api/person`,
                withCredentials: true,
                auth: createAuth(getState),
            });
            const persons = response.data.reduce((obj: any, item: any) => {
                obj[item.id] = { ...item, showRadial: false };
                return obj;
            }, {});
            return persons;
        } catch (error) {
            const err = error as any;
            if (err.response?.status === 401 || err.response?.status === 404) {
                return rejectWithValue({ redirectTo: '/login', message: err.response?.data || 'An error occurred' });
            }
            return rejectWithValue(err.response?.data || 'An error occurred');
        }
    }
);

export const checkIn = createAsyncThunk(
    'personState/checkIn',
    async (personId: string, { getState, rejectWithValue }) => {
        try {
            const response = await axios({
                method: 'POST',
                url: `${config.server}/api/person/checkin/${personId}`,
                withCredentials: true,
                auth: createAuth(getState),
            });
            return response.data;
        } catch (error) {
            const err = error as any;
            return rejectWithValue(err.response?.data || 'An error occurred');
        }
    }
);

export const checkOut = createAsyncThunk(
    'personState/checkOut',
    async (personId: string, { getState, rejectWithValue }) => {
        try {
            const response = await axios({
                method: 'POST',
                url: `${config.server}/api/person/checkout/${personId}`,
                withCredentials: true,
                auth: createAuth(getState),
            });
            return response.data;
        } catch (error) {
            const err = error as any;
            return rejectWithValue(err.response?.data || 'An error occurred');
        }
    }
);

const personSlice = createSlice({
    name: reducerName,
    initialState,
    reducers: {
        showRadial: (state, action: PayloadAction<{ personId: string; show: boolean }>) => {
            const { personId, show } = action.payload;
            if (state.persons[personId]) {
                state.persons[personId].showRadial = show;
            }
        },
        clearRedirect: (state) => {
            state.redirectAfterError = null;
        },
    },
    extraReducers: (builder) => {
        builder
            // getPerson
            .addCase(getPerson.pending, (state) => {
                state.status = 'loading';
                state.error = null;
            })
            .addCase(getPerson.fulfilled, (state, action) => {
                const person = action.payload;
                state.persons[person.id] = person;
                state.status = 'succeeded';
            })
            .addCase(getPerson.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.payload as string;
            })
            // getAllPersons
            .addCase(getAllPersons.pending, (state) => {
                state.status = 'loading';
                state.error = null;
            })
            .addCase(getAllPersons.fulfilled, (state, action) => {
                state.persons = action.payload;
                state.status = 'succeeded';
            })
            .addCase(getAllPersons.rejected, (state, action) => {
                state.status = 'failed';
                state.error = (action.payload as any)?.message || (action.payload as string);
                if ((action.payload as any)?.redirectTo) {
                    state.redirectAfterError = (action.payload as any).redirectTo;
                }
            })
            // checkIn
            .addCase(checkIn.pending, (state) => {
                state.status = 'loading';
                state.error = null;
            })
            .addCase(checkIn.fulfilled, (state, action) => {
                const updatedPerson = action.payload;
                state.persons[updatedPerson.id] = updatedPerson;
                state.status = 'succeeded';
            })
            .addCase(checkIn.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.payload as string;
            })
            // checkOut
            .addCase(checkOut.pending, (state) => {
                state.status = 'loading';
                state.error = null;
            })
            .addCase(checkOut.fulfilled, (state, action) => {
                const updatedPerson = action.payload;
                state.persons[updatedPerson.id] = updatedPerson;
                state.status = 'succeeded';
            })
            .addCase(checkOut.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.payload as string;
            });
    },
});

export const { showRadial, clearRedirect } = personSlice.actions;

export default personSlice.reducer;